import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { ChevronLeft, MapPin, Store } from 'lucide-react';
import { Button, Typography, Badge, Spinner } from 'glintly-ui';
import Container from '../../components/container';
import FallbackImage from '../../components/brand/FallbackImage';
import { useProduct, useProductsByShop } from '../../api';
import { addToCart } from '../../redux/slice/cartSlice';
import styles from './productDetail.module.css';

function getShopId(shop) {
  if (!shop) return null;
  if (typeof shop === 'object' && shop._id) return shop._id;
  if (typeof shop === 'string') return shop;
  return null;
}

function normalizeVariants(variants) {
  if (!variants) return [];
  if (Array.isArray(variants)) return variants;
  if (typeof variants === 'object') {
    return Object.entries(variants).map(([name, value]) => ({
      name,
      value: String(value),
    }));
  }
  return [];
}

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: product, isLoading: loading, error } = useProduct(id);

  const shopId = product ? getShopId(product.shop) : null;
  const { data: shopProducts = [], isLoading: suggestionsLoading } = useProductsByShop(shopId);

  const suggestions = useMemo(() => {
    if (!product?._id || !Array.isArray(shopProducts)) return [];
    return shopProducts.filter((p) => p && p._id && p._id !== product._id).slice(0, 8);
  }, [product, shopProducts]);

  const shop = product?.shop && typeof product.shop === 'object' ? product.shop : null;
  const variants = product ? normalizeVariants(product.variants) : [];
  const mainImage = product?.images?.[0] || '';

  const handleAddToCart = () => {
    if (!product) return;
    const sid = getShopId(product.shop);
    dispatch(
      addToCart({
        productId: product._id,
        shopId: sid,
        name: product.name,
        price: product.price,
        image: product.images && product.images[0],
        quantity: 1,
        variants: product.variants || {},
      })
    );
    toast.success(`${product.name} added to cart`);
  };

  return (
    <Container>
      <div className={styles.page}>
        {loading && (
          <div className={styles.stateCenter}>
            <Spinner />
            <Typography variant="body2" style={{ marginTop: 12 }}>
              Loading service…
            </Typography>
          </div>
        )}

        {error && (
          <div className={styles.stateCenter}>
            <Typography variant="body1" className={styles.errorText}>
              {String(error?.message || error)}
            </Typography>
            <Button
              variant="primary"
              style={{ marginTop: 16 }}
              onClick={() => navigate('/services')}
            >
              Back to services
            </Button>
          </div>
        )}

        {product && (
          <>
            <div className={styles.backRow}>
              <Link to="/services" className={styles.backLink}>
                <ChevronLeft size={18} aria-hidden />
                All services
              </Link>
            </div>

            <div className={styles.hero}>
              <div className={styles.gallery}>
                <FallbackImage src={mainImage} alt={product.name} />
              </div>

              <div className={styles.panel}>
                <div className={styles.chips}>
                  {product.category ? (
                    <Badge variant="secondary" size="sm">
                      {product.category}
                    </Badge>
                  ) : null}
                  {product.brand ? (
                    <Badge variant="gray" size="sm">
                      {product.brand}
                    </Badge>
                  ) : null}
                  {product.stock != null && Number(product.stock) <= 5 ? (
                    <Badge variant="warning" size="sm">
                      Only {product.stock} left
                    </Badge>
                  ) : null}
                </div>

                <h1 className={styles.title}>{product.name}</h1>

                <div className={styles.priceRow}>
                  <span className={styles.price}>₹{product.price}</span>
                  {product.stock != null ? (
                    <span className={styles.stockHint}>
                      {Number(product.stock) > 0 ? `${product.stock} in stock` : 'Out of stock'}
                    </span>
                  ) : null}
                </div>

                {product.description ? (
                  <p className={styles.description}>{product.description}</p>
                ) : null}

                {variants.length > 0 ? (
                  <div className={styles.metaGrid}>
                    <div className={styles.metaLabel}>Details</div>
                    {variants.map((v, i) => (
                      <div key={i} className={styles.metaValue}>
                        <strong>{v.name}:</strong>{' '}
                        {typeof v.value === 'object' ? JSON.stringify(v.value) : v.value}
                      </div>
                    ))}
                  </div>
                ) : null}

                {shop ? (
                  <div className={styles.shopCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Store size={18} color="var(--color-primary-600, #b91c1c)" />
                      <h2 className={styles.shopName}>{shop.name}</h2>
                    </div>
                    {shop.address?.fullAddress || shop.address?.city || shop.address?.landmark ? (
                      <p className={styles.shopAddress}>
                        <MapPin
                          size={14}
                          style={{
                            display: 'inline',
                            verticalAlign: 'middle',
                            marginRight: 4,
                          }}
                        />
                        {[
                          shop.address?.fullAddress,
                          shop.address?.landmark,
                          shop.address?.city,
                          shop.address?.postalCode,
                          shop.address?.country,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    ) : null}
                    <div className={styles.shopActions}>
                      {shopId ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/salon/${shopId}`)}
                        >
                          View salon
                        </Button>
                      ) : null}
                      {shopId ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/services/salon/${shopId}`)}
                        >
                          More from this salon
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {product.notes ? (
                  <div className={styles.metaGrid}>
                    <div className={styles.metaLabel}>Notes</div>
                    <div className={styles.metaValue}>{product.notes}</div>
                  </div>
                ) : null}

                <div className={styles.actions}>
                  <Button
                    variant="primary"
                    onClick={handleAddToCart}
                    disabled={product.stock != null && Number(product.stock) <= 0}
                  >
                    Add to cart
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/cart')}>
                    View cart
                  </Button>
                </div>
              </div>
            </div>

            {shopId ? (
              <section className={styles.suggestions} aria-labelledby="suggested-heading">
                <h2 id="suggested-heading" className={styles.suggestionsTitle}>
                  More from this salon
                </h2>
                <p className={styles.suggestionsSub}>
                  {suggestionsLoading
                    ? 'Loading suggestions…'
                    : suggestions.length
                      ? 'Customers often book these together.'
                      : 'No other services listed for this salon yet.'}
                </p>

                {!suggestionsLoading && suggestions.length > 0 ? (
                  <div className={styles.suggestionGrid}>
                    {suggestions.map((p) => (
                      <Link key={p._id} to={`/services/${p._id}`} className={styles.suggestionCard}>
                        <div className={styles.suggestionImgWrap}>
                          <FallbackImage src={p.images?.[0]} alt="" />
                        </div>
                        <div className={styles.suggestionBody}>
                          <p className={styles.suggestionName}>{p.name}</p>
                          <p className={styles.suggestionPrice}>₹{p.price}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </div>
    </Container>
  );
};

export default ProductDetail;
